import React, { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import Text from "../../components/Text";
import Button from "../../components/Button";
import Column from "../../components/Column";
import { INote } from "../../interfaces/models/note";
import PrivateCheckBoxControl from "../PrivateCheckBoxControl";
import DropdownMenu from "../../components/dropdown/DropdownMenu";
import { styles } from "../../styles/themes/style";
import ContentEditable from 'react-contenteditable'
import { sanitize } from "../../utils";
import { ICreateNote, useNotesHandlers } from "../../services/actions/notes/useNotesHandlers";
import Row from "../../components/Row";
import ProfilePhoto from "../../components/ProfilePhoto";
import { useAuthStore } from "../../stores/auth/useAuthStore"
import { useAppStore } from "../../stores/application/useAppStore";
import moment from "moment";
import EditDeleteActionDropdown from "../dropdowns/EditDeleteActionDropdown";
import { useAppHandlers } from "../../services/actions/app/useAppHandlers";
import ActionText from "../../components/ActionText";
import { IConnection } from "../../interfaces/models/connection";

interface INoteSidebarProps {
    note?: INote
    connectionId?: string
    existingNotes?: any
    editNote?: boolean
    duplicate?: boolean
    showEmptyPage?: boolean
    addScrollOffset?: boolean
    onCloseHandler?: () => void
    onCreateHandler?: (noteId: string | null, note: ICreateNote, updateNotes?: (notes: any) => void) => void
    onCreatedNoteHandler?: (updatedConnection: IConnection) => void
    inboxSurface?: boolean
    embeddedNoteFormSurface?: boolean
}
interface ICustomTextAreaStyles {
    $isSelected?: boolean
}

const NoteFormSurface = styled.div<{ $surface?: boolean }>`
    ${({ $surface }) => $surface ? `
        background-color: ${styles.colors.white};
        border-radius: 4px;
        padding: 12px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        ` : `display: contents;`
    }
`

const placeCaretAfterMentionTag = (contentEditableDiv: HTMLDivElement, tagId: string) => {
    const tag = contentEditableDiv.querySelector(
        `span[data-id="${CSS.escape(tagId)}"][data-type="tag"]`
    )
    if (!tag) return

    contentEditableDiv.focus()
    const selection = window.getSelection()
    if (!selection) return

    const range = document.createRange()
    const afterTag = tag.nextSibling

    if (afterTag?.nodeType === Node.TEXT_NODE && afterTag.textContent) {
        range.setStart(afterTag, afterTag.textContent.length)
    } else {
        range.setStartAfter(tag)
    }

    range.collapse(true)
    selection.removeAllRanges()
    selection.addRange(range)
}

const CreateEditNoteForm: FunctionComponent<INoteSidebarProps> = ({note, connectionId, existingNotes, editNote, duplicate = false, showEmptyPage = false, addScrollOffset, onCloseHandler, onCreateHandler, onCreatedNoteHandler, inboxSurface = false, embeddedNoteFormSurface = false}) => {
    const newNoteTextAreaRef = useRef<HTMLDivElement>(null)
    const editNoteTextAreaRef = useRef<HTMLDivElement>(null)
    const dropdownAnchorRef = useRef<HTMLDivElement>(null)
    const pendingCaretAfterTagRef = useRef<{ tagId: string; containerRef: React.RefObject<HTMLDivElement> } | null>(null)
    const textRef = useRef('')
    const { store: { workspace} } = useAuthStore()

    const { workspaceUsersPublicAllHandler, deleteNoteHandler, createUpdateNoteHandler } = useNotesHandlers()
    const { setDestructiveModal, hideModal } = useAppHandlers()

    const removeHtmlTags = (str: any) => { return str?.length && str.replace(/<\/?[^>]+(>|$)/g, "") }

    const [noteState, setNoteState] = useState<any>(note ? {_id: note?._id, text: note?.text, private: note?.private} : {_id: undefined, text: ' ', private: true})
    const [showInputDropdown, setShowInputDropdown] = useState<boolean>(false)
    const [dropdownPosition, setDropdownPosition] = useState<any>({ top: 0, left: 0 })
    const [submitLoading, setSubmitLoading] = useState<boolean>(false)
    const { store: { requestLoading } } = useAppStore()

    const [addNoteState, setAddNoteState] = React.useState(editNote || !showEmptyPage);
    const [editingNote, setEditingNote] = useState<any>();
    const [existingNotesState, setExistingNotesState] = useState<any>(existingNotes);
    const [addingNote, setAddingNote] = React.useState(false);
    const [disabledCheckbox, setDisabledCheckbox] = React.useState(false);
    
    /* TODO: This can be done better */
    const [workspaceUsers, setWorkspaceUsers] = useState<any>([])
    const [filteredWorkspaceUsers, setFilteredWorkspaceUsers] = useState<any>([])

    const workspaceUsersHandler = async () => {
        const result = await workspaceUsersPublicAllHandler({
            condition: 'or',
            name: '',
            email: '' 
        })
        setWorkspaceUsers(result)
    }

    useEffect(() => {
        if(editingNote?.text?.length) {
            if(typeof editingNote.text === 'string') {
                textRef.current = editingNote.text
            } else {
                textRef.current = editingNote.text[0]
            }
        }
    }, [editingNote])

    useEffect(() => {
        if(note?.text?.length) {
            if(typeof note.text === 'string') {
                if(duplicate) {
                    textRef.current = `Copy ${note.text}`
                } else {
                    textRef.current = note.text
                }
            } else {
                if(duplicate) {
                    textRef.current = `Copy ${note.text[0]}`
                } else {                    
                    textRef.current = note.text[0]
                }
            }
        }
    }, [note])

    useEffect(() => {
        workspaceUsersHandler()
    }, [])

    useEffect(() => {
        setExistingNotesState(existingNotes);
    }, [existingNotes])
    
    useEffect(() => {
        setFilteredWorkspaceUsers(workspaceUsers)
    }, [workspaceUsers])

    useLayoutEffect(() => {
        const pending = pendingCaretAfterTagRef.current
        if (!pending) return

        const { tagId, containerRef } = pending
        pendingCaretAfterTagRef.current = null

        requestAnimationFrame(() => {
            const editable = containerRef.current
            if (editable) {
                placeCaretAfterMentionTag(editable, tagId)
            }
        })
    })

    useEffect(() => {
        if('createNote' in requestLoading){
            setSubmitLoading(requestLoading?.['createNote'])
        }
        if(!requestLoading?.['createNote']){
            if('updateNote' in requestLoading){
                setSubmitLoading(requestLoading?.['updateNote'])
            }
        }
    }, [requestLoading]);

    const getCaretCoordinates = (contentEditableDiv: HTMLDivElement) => {
        const anchorEl = dropdownAnchorRef.current
        const anchorRect = anchorEl?.getBoundingClientRect()
        const textareaRect = contentEditableDiv.getBoundingClientRect()

        let caretRect = textareaRect
        const selection = window.getSelection()

        if (selection && selection.rangeCount > 0 && contentEditableDiv.contains(selection.anchorNode)) {
            const range = selection.getRangeAt(0).cloneRange()
            range.collapse(true)
            caretRect = range.getBoundingClientRect()

            if (!caretRect.width && !caretRect.height) {
                caretRect = new DOMRect(textareaRect.left + 10, textareaRect.bottom - 4, 0, 0)
            }
        }

        if (!anchorRect) {
            return {
                top: caretRect.bottom - textareaRect.top + 4,
                left: caretRect.left - textareaRect.left,
            }
        }

        let top = caretRect.bottom - anchorRect.top + 4
        if (addScrollOffset) {
            top += getScrollOffset(contentEditableDiv) - 520
        }

        return {
            top,
            left: Math.max(0, caretRect.left - anchorRect.left),
        }
    }

    const setTaggedConnectionHandler = (workspaceUser: any, containerRef: React.RefObject<HTMLDivElement>) => {
        const textCurrent = textRef.current
        const id = workspaceUser._id
        const name = workspaceUser?.firstName + ' ' + workspaceUser.lastName

        const tagSpan = `&nbsp;<span data-id="${id}" data-object="user" data-type="tag" data-label="@${name}" class="ld-tag">@${name}</span>&nbsp;`

        if(textCurrent.endsWith('@')) textRef.current = textCurrent.slice(0, -1) + tagSpan
        else textRef.current = textCurrent.replace(/@[\w]+(?!.*@[\w]+)/, '') + tagSpan

        setFilteredWorkspaceUsers(workspaceUsers)
        setNoteState((previousState: any) => ({ ...previousState, private: false }))
        setShowInputDropdown(false)
        setDisabledCheckbox(true)

        pendingCaretAfterTagRef.current = { tagId: id, containerRef }
    }

    const onDeleteNoteHandler = async (noteId: string) => {
        setDestructiveModal({
            headingText: 'Delete note',
            descriptionText: "Are you sure you want to delete this note?",
            onCancelHandler: () => hideModal(),
            onConfirmHandler: async () => {
                hideModal()
                deleteNoteConfirmed(noteId)
            }
        })
    }

    const deleteNoteConfirmed = async (noteId: string) => {
        const result = await deleteNoteHandler(noteId)
        if(result?.success){
            setExistingNotesState(result.data?.notes)
        }
    }

    const getScrollOffset = (element: HTMLElement): number => {
        let scrollOffset = 0;
        let parent = element?.parentElement;
    
        while (parent) {
            scrollOffset += parent?.scrollTop;
            parent = parent?.parentElement;
        }
    
        return scrollOffset;
    };

    const handleChange = (e: any, containerRef: React.RefObject<HTMLDivElement>) => {
        const div = containerRef?.current;

        const inputString = e?.target?.value || ''
        const stringWithoutHtml = e?.target?.value.replace(/<\/?[^>]+(>|$)/g, "")
        const stringWithSpaces = stringWithoutHtml.replace(/&nbsp;/g, " ")
        const words = stringWithSpaces.trim().split(/\s+/);
        const lastWord = words[words.length - 1]
        const lastWordStartsWithAt = lastWord.startsWith('@')

        if(div) {
            if(e.nativeEvent.inputType === 'deleteContentBackward' && (removeHtmlTags(inputString).length === 0 || removeHtmlTags(inputString).length === undefined)) {
                textRef.current = '&nbsp;'

                setShowInputDropdown(false)
            } else if(e.nativeEvent.data === '@' && workspace.isBusiness) {
                textRef.current = e.target.value 

                const { top, left } = getCaretCoordinates(div)
                setDropdownPosition({ top, left })

                setShowInputDropdown(true)
            } else if(lastWordStartsWithAt && workspace.isBusiness) {
                setShowInputDropdown(true)

                textRef.current = e.target.value

                const { top, left } = getCaretCoordinates(div)
                setDropdownPosition({ top, left })

                const lowerCaseQuery = lastWord.replace(/^@/, '').toLowerCase()
                const filtered = workspaceUsers.filter((workspaceUser: any) => workspaceUser.firstName.toLowerCase().startsWith(lowerCaseQuery) || workspaceUser.lastName.toLowerCase().startsWith(lowerCaseQuery))

                if(filtered.length === 0) setShowInputDropdown(false)

                setFilteredWorkspaceUsers(filtered)
            } else {
                setShowInputDropdown(false)

                textRef.current = e.target.value
            }
        }

        if(containTags(inputString)){
            setDisabledCheckbox(true);
        } else {
            setDisabledCheckbox(false);
        }
    }

    const createUpdateNote = async () => {
        setAddingNote(true); 
        setSubmitLoading(true);

        let body = note ?  
        {
            private: noteState.private,
            text: [textRef.current],
            connection: note?.connection?._id
        } : {
            connection: connectionId,
            private: noteState?.private,
            text: [textRef.current]
        }

        if(duplicate){
            setNoteState((previousState: any) => ({ ...previousState, _id: undefined }));
        }

        if(onCreateHandler){
            onCreateHandler(duplicate ? undefined : noteState?._id, body, (updatedNotes) => {
                if(updatedNotes){
                    setExistingNotesState(updatedNotes);
                }
            });
        }
        else {
            const result = await createUpdateNoteHandler(duplicate ? undefined : noteState?._id, body);
            if(result){
                setExistingNotesState(result.notes);
                onCreatedNoteHandler && onCreatedNoteHandler(result);
            }
        }

        if(editingNote){
            let updatedNotes = existingNotesState.map((note: any) => {
                if (note._id === editingNote._id) {
                    return {...note, text: [textRef.current], private: noteState?.private};
                } else {
                    return note;
                }
            })
            setExistingNotesState(updatedNotes);
        }
        setEditingNote(undefined);
        setAddNoteState(!showEmptyPage);
        setAddingNote(false); 
        setSubmitLoading(false);
        setNoteState({_id: undefined, text: ' ', private: true});
        textRef.current = '';
        setDisabledCheckbox(false);
    }

    const onEditNoteHandler = async (note: any) => {
        setAddNoteState(false);
        setNoteState(note);
        setEditingNote(note);
        if(note?.text?.length) {
            if(typeof note.text === 'string') {
                textRef.current = note.text
            } else {
                textRef.current = note.text[0]
            }
            if(containTags(note.text)){
                setDisabledCheckbox(true);
            }
        } 
    }

    const onCloseEditingNoteHandler = async () => { 
        setEditingNote(undefined);
        setAddNoteState(true);
        setNoteState({_id: undefined, text: ' ', private: true}); 
        textRef.current = '';
    }

    const onCloseAddingNoteHandler = async () => {
        setAddNoteState(false); 
        setEditingNote(undefined);
        onCloseHandler && onCloseHandler();
    }

    const containTags = (input: any) => {
        const noteText = (Array.isArray(input) ? input.join('') : input);
        return noteText?.includes('data-type="tag"');
    }

    return (
        <StyledColumn ref={dropdownAnchorRef} $gap="10px">
                     <Row spaceBetween>
                     <Text $heading6>{'Notes'}</Text>
                     {existingNotesState?.length > 0 && <ActionText 
                        onClickHandler={() => {setAddNoteState(true); setEditingNote(undefined); setNoteState({_id: undefined, text: ' ', private: true}); textRef.current = '';}}
                            >{'Add note'}
                        </ActionText>
                     }
                    </Row>
            {addNoteState && (!editingNote || editNote) &&
                <NoteFormSurface $surface={embeddedNoteFormSurface}>
                    <StyledTextarea
                    innerRef={newNoteTextAreaRef}
                    html={sanitize(textRef.current)}
                    disabled={false}
                    onChange={(e: any) => {handleChange(e, newNoteTextAreaRef);}}
                    />
                    <StyledDropdown show={showInputDropdown} forceShow={showInputDropdown} top={dropdownPosition.top} left={dropdownPosition.left}>
                        {filteredWorkspaceUsers.map((workspaceUser: any, index: number) => (
                            <DropdownItem
                                key={index}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setTaggedConnectionHandler(workspaceUser, newNoteTextAreaRef)}
                            >
                                <Row alignItems gap="10px">
                                    <ProfilePhoto capitalLetters={`${workspaceUser.firstName.charAt(0).toUpperCase()}${workspaceUser.lastName.charAt(0).toUpperCase()}`} />
                                    <Column>
                                        <Text>{workspaceUser.firstName + ' ' + workspaceUser.lastName}</Text>
                                        <Text>{workspaceUser.email}</Text>
                                    </Column>
                                </Row>
                            </DropdownItem>
                        ))}
                    </StyledDropdown>
                    {!workspace.isStarter && <Text $paragraph3>Tagging someone will automatically make this note public</Text>}
                    { 
                    workspace.isBusiness && <PrivateCheckBoxControl
                            type='note'
                            value={noteState?.private}
                            disabled={disabledCheckbox}
                            onChangeHandler={(value: boolean) => setNoteState((previousState: any) => ({ ...previousState, private: value }))}
                        /> 
                        }
                        <ButtonWrapper>
                            <Button $type="cancel" onClickHandler={() => onCloseAddingNoteHandler()}>Cancel</Button>
                            <Button $type="blue" onClickHandler={createUpdateNote} isLoading={submitLoading}>Save</Button>
                        </ButtonWrapper>
                </NoteFormSurface>
                    }
                    {existingNotesState?.length > 0 ? (existingNotesState?.map((note: any) => {
                            return (
                                editingNote && editingNote._id === note._id ? (
                                    <NoteFormSurface $surface={embeddedNoteFormSurface} key={note._id}>
                                        <StyledTextarea
                                        innerRef={editNoteTextAreaRef}
                                        html={sanitize(textRef.current)}
                                        disabled={false}
                                        onChange={(e: any) => {handleChange(e, editNoteTextAreaRef);}}
                                        />
                                            <StyledDropdown show={showInputDropdown} forceShow={showInputDropdown} top={dropdownPosition.top} left={dropdownPosition.left}>
                                                {filteredWorkspaceUsers.map((workspaceUser: any, index: number) => (
                                                    <DropdownItem
                                                        key={index}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        onClick={() => setTaggedConnectionHandler(workspaceUser, editNoteTextAreaRef)}
                                                    >
                                                        <Row alignItems gap="10px">
                                                            <ProfilePhoto capitalLetters={`${workspaceUser.firstName.charAt(0).toUpperCase()}${workspaceUser.lastName.charAt(0).toUpperCase()}`} />
                                                            <Column>
                                                                <Text>{workspaceUser.firstName + ' ' + workspaceUser.lastName}</Text>
                                                                <Text>{workspaceUser.email}</Text>
                                                            </Column>
                                                        </Row>
                                                    </DropdownItem>
                                                ))}
                                            </StyledDropdown>
                                            {!workspace.isStarter && <Text $paragraph3>Tagging someone will automatically make this note public</Text>}
                                        { 
                                        workspace.isBusiness && <PrivateCheckBoxControl
                                                type='note'
                                                value={noteState?.private}
                                                disabled={disabledCheckbox}
                                                onChangeHandler={(value: boolean) => setNoteState((previousState: any) => ({ ...previousState, private: value }))}
                                            /> 
                                            }
                                            <ButtonWrapper>
                                                <Button $type="cancel" onClickHandler={() => onCloseEditingNoteHandler()}>Cancel</Button>
                                                <Button $type="blue" onClickHandler={createUpdateNote} isLoading={submitLoading}>Save</Button>
                                            </ButtonWrapper>
                                    </NoteFormSurface>
                                ) : (
                                    <StyledNoteRow spaceBetween key={note._id} $inboxSurface={inboxSurface}>
                                        <StyledNoteColumn>
                                            <Text $bold $black>{<InnerHtml dangerouslySetInnerHTML={{ __html: sanitize(note.text) }} />}</Text>
                                            <Text $label $lighter>{moment(note.createdAt).format('MMM D, YYYY HH:mm')}</Text>
                                        </StyledNoteColumn>
                                        <EditDeleteActionDropdown
                                            object={note}
                                            type='note'
                                            onDeleteHandler={() => onDeleteNoteHandler(note._id)}
                                            onEditHandler={() => onEditNoteHandler(note)}
                                        />
                                    </StyledNoteRow>
                                 )
                            );
                        }
                    )
                ) : (!editingNote && !addNoteState && !addingNote && !editNote && showEmptyPage && (
                    <EmptyListWrapper $inboxSurface={inboxSurface}>
                        <EmptyListLabel>No notes to show</EmptyListLabel>
                        <EmptyListButton onClick={() => setAddNoteState(true)}>Add note</EmptyListButton>
                    </EmptyListWrapper>
                    )   
                )
            }
        </StyledColumn>
    )
}
const StyledColumn = styled.div<{ $gap?: string }>`
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    ${({ $gap }) => $gap && `gap: ${$gap};`}
`
const StyledTextarea = styled(ContentEditable)<ICustomTextAreaStyles>`
    width: 100%;
    height: 100px;
    overflow-y: auto;
    border: 2px solid ${({ $isSelected }) => $isSelected ? styles.colors.primary600 : styles.colors.black200};
    border-radius: 4px;
    padding: 10px;
    resize: none;

    font-family: "Nunito Sans";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    color: #333951;

    & .ld-tag {
        background-color: ${styles.colors.primary100};
        color: #5964ff;
    }
    
    & p {
        margin: 0;
    }
`
const ButtonWrapper = styled.div`
    display: flex;
    justify-content: end;
    gap: 10px;
`
const StyledDropdown = styled(DropdownMenu)<{ top: number; left: number }>`
    position: absolute;
    top: ${(props) => props.top + 18}px;
    left: ${(props) => props.left}px;
    background-color: white;
    border: 1px solid #ccc;
    z-index: 1000;

    & svg { display: none }
`
const DropdownItem = styled.div`
    padding: 10px;
    cursor: pointer;

    &:hover {
        background-color: #f0f0f0;
    }
`
const StyledNoteRow = styled(Row)<{ $inboxSurface?: boolean }>`
    width: 100%;
    padding: 10px;
    background: ${({ $inboxSurface }) => ($inboxSurface ? styles.colors.white : styles.colors.black100)};
`
const StyledNoteColumn = styled(Column)`
    gap: 10px;
    overflow: auto;
`
const InnerHtml = styled.span`
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`
const EmptyListWrapper = styled.div<{ $inboxSurface?: boolean }>`
    height: 86px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-evenly;
    border-radius: 4px;
    background-color: ${({ $inboxSurface }) => ($inboxSurface ? styles.colors.white : styles.colors.black100)};
`
const EmptyListLabel = styled.p`
    font-weight: 700;
    line-height: 14px;
    font-size: 11px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${styles.colors.black300};
    font-family: NunitoSans;
`
const EmptyListButton = styled.p`
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0px;
    text-align: center;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    color: ${({theme: {secondaryColor}}) => secondaryColor};
    background-color: ${styles.colors.primary600};
`
export default CreateEditNoteForm